/**
 * Authentication Context
 * 
 * Provides auth state management across the application.
 * Handles session storage, token expiration tracking, and auto-redirect.
 * 
 * Requirements: 4.2, 4.5
 */

import { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  useCallback
} from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthSession, JWTPayload } from '../utils/auth';
import { 
  storeSession, 
  getSession, 
  clearSession,
  validateToken,
  decodeJWT,
  canEditProducts
} from '../utils/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  session: AuthSession | null;
  user: AuthSession['user'] | null;
  tokenPayload: JWTPayload | null;
  hasEditProductsCapability: boolean;
  login: (session: AuthSession) => void;
  logout: () => void;
  checkSession: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Check session validity every 30 seconds
const SESSION_CHECK_INTERVAL = 30 * 1000;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Derive token payload from session
  const tokenPayload = session?.token ? decodeJWT(session.token) : null;
  
  // Check if user has edit_products capability
  const hasEditProductsCapability = canEditProducts(tokenPayload);

  /**
   * Check if current session is valid
   */
  const checkSession = useCallback((): boolean => {
    const storedSession = getSession();
    
    if (!storedSession) {
      return false;
    }

    const validation = validateToken(storedSession.token);
    return validation.valid;
  }, []);

  /**
   * Handle login - store session and update state
   */
  const login = useCallback((newSession: AuthSession) => {
    storeSession(newSession);
    setSession(newSession);
  }, []);

  /**
   * Handle logout - clear session and redirect to login
   */
  const logout = useCallback(() => {
    clearSession();
    setSession(null);
    navigate('/login');
  }, [navigate]);

  /**
   * Initialize auth state from session storage on mount
   */
  useEffect(() => {
    const initializeAuth = () => {
      const storedSession = getSession();
      
      if (storedSession) {
        const validation = validateToken(storedSession.token);
        
        if (validation.valid) {
          setSession(storedSession);
        } else {
          // Session expired or invalid, clear it
          clearSession();
        }
      }
      
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  /**
   * Set up periodic session validity check
   * Auto-redirect to login on token expiry (Requirement 4.5)
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    const checkAndHandleExpiry = () => {
      const validation = validateToken(session.token);
      
      if (!validation.valid) {
        // Token expired or invalid
        logout();
      }
    };

    // Check immediately
    checkAndHandleExpiry();

    // Set up interval for periodic checks
    const intervalId = setInterval(checkAndHandleExpiry, SESSION_CHECK_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [session, logout]);

  const value: AuthContextType = {
    isAuthenticated: !!session && checkSession(),
    isLoading,
    session,
    user: session?.user ?? null,
    tokenPayload,
    hasEditProductsCapability,
    login,
    logout,
    checkSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

export default AuthContext;
